import React, { useState, useEffect } from 'react';
import '../css/News.css';

// Safely resolve assets from src or public
function resolveAsset(name) {
	try {
		const req = require.context('../', true, /\.(svg|png|jpe?g|webp)$/i);
		const match = req.keys().find((k) => k.toLowerCase().includes(name.toLowerCase()));
		if (match) {
			const res = req(match);
			return res.default || res;
		}
	} catch (e) {
		// ignore
	}
	return null;
}

function getNewsImage(item) {
	if (!item) return null;
	if (item.image) return resolveAsset(item.image) || item.image;

	// Parse date components (e.g. "06 Feb 2026" or "25 Feb 2026")
	const parts = (item.date || '').toLowerCase().split(/\s+/).filter(Boolean);
	const rawDay = parts[0] || '';
	const dayWithZero = rawDay.padStart(2, '0');
	const dayNoZero = String(parseInt(rawDay, 10) || rawDay);
	const month = parts[1] || '';

	try {
		const req = require.context('../', true, /\.(svg|png|jpe?g|webp)$/i);
		const keys = req.keys();

		// Filter for files with "news" in the filename
		const newsFiles = keys.filter((k) => {
			const filename = k.split('/').pop().toLowerCase();
			return filename.includes('news');
		});

		// 1. Match both month AND specific day (supports "6", "06", or "8", "08")
		if (month && rawDay) {
			const dayPatterns = [dayWithZero, dayNoZero];
			// Also support 6 and 8 interchange if dates differ between frontend/backend
			if (dayNoZero === '6' || dayNoZero === '8') {
				dayPatterns.push('6', '06', '8', '08');
			}

			const exactMatch = newsFiles.find((k) => {
				const filename = k.split('/').pop().toLowerCase();
				return filename.includes(month) && dayPatterns.some((d) => filename.includes(d));
			});
			if (exactMatch) {
				const res = req(exactMatch);
				return res.default || res;
			}
		}

		// 2. Match by id if file is named like news-2 or news-item-2
		if (item.id) {
			const idMatch = newsFiles.find((k) => {
				const filename = k.split('/').pop().toLowerCase();
				return filename.includes(`news-${item.id}`) || filename.includes(`news_${item.id}`);
			});
			if (idMatch) {
				const res = req(idMatch);
				return res.default || res;
			}
		}

		// 3. Match by distinctive title keywords
		const titleLower = (item.title || '').toLowerCase();
		const titleKeywords = titleLower.includes('matter')
			? ['matter', 'panel']
			: titleLower.includes('school')
			? ['school', 'scientist']
			: titleLower.includes('cancer') || titleLower.includes('career')
			? ['cancer', 'career', 'presentation']
			: [];

		if (titleKeywords.length > 0) {
			const kwMatch = newsFiles.find((k) => {
				const filename = k.split('/').pop().toLowerCase();
				return titleKeywords.some((kw) => filename.includes(kw));
			});
			if (kwMatch) {
				const res = req(kwMatch);
				return res.default || res;
			}
		}
	} catch (e) {
		// ignore
	}
	return null;
}

const DEFAULT_NEWS = [
	{
		id: 1,
		title: 'Scientists In Schools Workshop',
		date: '25 Feb 2026',
		color: 'yellow',
		content:
			"Yesterday was our Scientists in Schools workshop! OSSCE members had the chance to learn from an expert how best to share their work with younger generations. ",
	},
	{
		id: 2,
		title: 'Making Research Matter',
		date: '06 Feb 2026',
		color: 'coral',
		content:
			'What exactly is public engagement, and why does it matter? In our flagship event this term, OSSCE gathered experts on the topic for a panel discussion hosted in the beautiful Holywell Music Room.',
	},
	{
		id: 3,
		title: 'Early Career Scientists Presentation',
		date: '05 Oct 2025',
		color: 'teal',
		content:
			'Just over a week ago, early-career cancer researchers were given the opportunity to present their work to a range of non-specialists at an Oxford Cancer Group meeting.',
	},
];

function News() {
	const [news, setNews] = useState(DEFAULT_NEWS);

	useEffect(() => {
		fetch('https://ossce-webbackend.ossce-web.workers.dev/api/news')
			.then((res) => res.json())
			.then((data) => {
				if (Array.isArray(data) && data.length > 0) {
					setNews(data);
				}
			})
			.catch(() => {
				// Keep DEFAULT_NEWS on error
			});
	}, []);

	return (
		<div className="news-page-wrapper">
			<div className="news-container">
				<span className="news-eyebrow">NEWS</span>
				<h1 className="news-title">Latest news</h1>
				<div className="news-yellow-bar" />
				<p className="news-subhead-p">
					Find out all about what we at OSSCE have been getting up to.
				</p>

				<div className="news-grid">
					{news.map((item) => {
						const headerColorClass =
							item.color === 'coral' || item.color === 'orange'
								? 'header-coral'
								: item.color === 'teal'
								? 'header-teal'
								: 'header-yellow';

						const newsImg = getNewsImage(item);

						return (
							<div key={item.id} className="news-card">
								<div className={`news-card-header ${headerColorClass}`}>
									<span className="news-card-title">{item.title}</span>
									{/* <span className="news-card-date">{item.date}</span> */}
								</div>

								<div className="news-card-thumb">
									{newsImg ? (
										<img
											src={newsImg}
											alt={item.title}
											className="news-card-thumb-img"
											onError={(e) => {
												e.currentTarget.style.display = 'none';
												if (e.currentTarget.nextSibling) {
													e.currentTarget.nextSibling.style.display = 'flex';
												}
											}}
										/>
									) : null}
									<div
										className="news-card-thumb-fallback"
										style={{
											display: newsImg ? 'none' : 'flex',
											position: 'relative',
											alignItems: 'center',
											justifyContent: 'center',
											width: '100%',
											height: '100%',
										}}
									>
										<svg
											width="34"
											height="34"
											viewBox="0 0 24 24"
											fill="none"
											stroke="#2563eb"
											strokeWidth="1.6"
										>
											<circle cx="12" cy="12" r="10"></circle>
											<line x1="2" y1="12" x2="22" y2="12"></line>
											<path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
										</svg>
										<span
											style={{
												position: 'absolute',
												background: '#f59e0b',
												color: '#111',
												fontSize: '7px',
												fontWeight: '800',
												padding: '1px 3px',
												borderRadius: '2px',
												boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
											}}
										>
											EVENT
										</span>
									</div>
								</div>

								<div className="news-card-body">
									<p className="news-card-content">{item.content}</p>
								</div>
							</div>
						);
					})}
				</div>

				<div className="load-more-wrap">
					<button className="btn-load-more">LOAD MORE</button>
				</div>
			</div>
		</div>
	);
}

export default News;
